import { Route, Switch } from 'wouter';
import * as React from 'react';
import { ContextRedirect } from './components/contexts/ContextRedirect';
import { ContextRouteGuard } from './components/contexts/ContextRouteGuard';
import { lazyWithRetry } from './lib/lazy-with-retry';

// Lazy load page components with retry logic
const InboxPage = lazyWithRetry(() => import('./pages/inbox/inbox'), 'InboxPage');
const LogsPage = lazyWithRetry(() => import('./pages/logs/logs'), 'LogsPage');
const RecurringPage = lazyWithRetry(() => import('./pages/recurring/recurring'), 'RecurringPage');
const ScheduledPage = lazyWithRetry(() => import('./pages/scheduled/scheduled'), 'ScheduledPage');
const ProfilePage = lazyWithRetry(() => import('./pages/profile/profile'), 'ProfilePage');
const ContextSettingsPage = lazyWithRetry(() => import('./pages/context-settings/context-settings'), 'ContextSettingsPage');
const TodayPage = lazyWithRetry(() => import('./pages/today/today'), 'TodayPage');
const ManageCategoriesPage = lazyWithRetry(
  () => import('./pages/manage-categories/manage-categories'),
  'ManageCategoriesPage'
);
const CategoryPage = lazyWithRetry(() => import('./pages/category/category'), 'CategoryPage');
const ContextsPage = lazyWithRetry(() => import('./pages/contexts/contexts'), 'ContextsPage');
const CreateContextPage = lazyWithRetry(() => import('./pages/create-context/create-context'), 'CreateContextPage');
const ProjectsPage = lazyWithRetry(() => import('./pages/projects/projects'), 'ProjectsPage');
const ProjectDetailPage = lazyWithRetry(() => import('./pages/project-detail/project-detail'), 'ProjectDetailPage');
const PriorityPage = lazyWithRetry(() => import('./pages/priority/priority'), 'PriorityPage');
const JoinContextPage = lazyWithRetry(() => import('./pages/join-context/join-context'), 'JoinContextPage');
const AllPage = lazyWithRetry(() => import('./pages/all/all'), 'AllPage');
const GoalsPage = lazyWithRetry(() => import('./pages/goals/goals'), 'GoalsPage');

// Memoize the fallback component
const LoadingFallback = React.memo(() => (
  <div className="flex items-center justify-center h-screen">Loading...</div>
));

// Memoize 404 component
const NotFound = React.memo(() => <div>404: Page Not Found</div>);

// Context-aware page wrapper
const ContextPageWrapper = React.memo(({ 
  Component, 
  contextId 
}: { 
  Component: React.ComponentType<{ contextId: string }>;
  contextId: string;
}) => (
  <ContextRouteGuard contextId={contextId}>
    <Component contextId={contextId} />
  </ContextRouteGuard>
));

const AppRouter = React.memo(() => {
  return (
    <React.Suspense fallback={<LoadingFallback />}>
      <Switch>
        {/* Root redirects to default context */}
        <Route path="/" component={ContextRedirect} />
        
        {/* Shortcut redirects to active context */}
        <Route path="/projects" component={() => <ContextRedirect targetPath="projects" />} />
        <Route path="/priority" component={() => <ContextRedirect targetPath="priority" />} />
        <Route path="/all" component={() => <ContextRedirect targetPath="all" />} />
        <Route path="/goals" component={() => <ContextRedirect targetPath="goals" />} />
        {/* Legacy dashboard route - redirect to goals */}
        <Route path="/dashboard" component={() => <ContextRedirect targetPath="goals" />} />
        
        {/* User profile route */}
        <Route path="/profile" component={ProfilePage} />
        
        {/* Context management routes */}
        <Route path="/contexts" component={ContextsPage} />
        <Route path="/contexts/create" component={CreateContextPage} />
        <Route path="/contexts/join/:token" component={JoinContextPage} />
        <Route path="/contexts/join" component={JoinContextPage} />
        
        {/* Context-scoped application routes */}
        <Route path="/context/:contextId/inbox/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={InboxPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/today/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={TodayPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/scheduled/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={ScheduledPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/recurring/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={RecurringPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/logs">
          {({ contextId }) => (
            <ContextPageWrapper Component={LogsPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/categories/manage">
          {({ contextId }) => (
            <ContextPageWrapper Component={ManageCategoriesPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/category/:categoryId/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={CategoryPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/projects/:projectId/:taskId?">
          {({ contextId }) => (
            <ContextPageWrapper Component={ProjectDetailPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/projects">
          {({ contextId }) => (
            <ContextPageWrapper Component={ProjectsPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/priority/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={PriorityPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/all/:rest*?">
          {({ contextId }) => (
            <ContextPageWrapper Component={AllPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/settings">
          {({ contextId }) => (
            <ContextPageWrapper Component={ContextSettingsPage} contextId={contextId} />
          )}
        </Route>
        <Route path="/context/:contextId/goals">
          {({ contextId }) => (
            <ContextPageWrapper Component={GoalsPage} contextId={contextId} />
          )}
        </Route>
        
        <Route component={NotFound} />
      </Switch>
    </React.Suspense>
  );
});

AppRouter.displayName = 'AppRouter';

export default AppRouter;
